/* eslint-disable max-lines */
import { Container, Card, Row, Col, Input, Button, Label, FormGroup, CardHeader, CardBody } from 'reactstrap';
import { useContext, useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import CommonHeader from '../../../../components/Headers/CommonHeader';
import EmployeeService from '../../../../services/Employee';
import AnnualReviewService from '../../../../services/AnnualReview';
import { PagedMetaInterface, createPagedMeta } from '../../../../entities/PagedMeta';
import DataTable from '../../../../components/Core/DataTableWithoutHeader';
import moment from 'moment';
import EmployeeTerminate from '../../../../services/EmployeeTerminate';
import confirmAlert from '../../../../helpers/confirmAlert';
import { confirmAlert as customAlert } from 'react-confirm-alert';
import TerminatedEmployeeFormV2 from '../TerminatedEmployeeFormV2';
import RevokeOfferForm from '../RevokeOfferForm';
import RevokeTerminationForm from '../RevokeTerminationForm';
import RehireForm from '../RehireForm';
import { useHistory, useParams } from 'react-router-dom';
import useCheckMobileScreen from '../../../../hooks/useCheckMobileScreen';
import ModalProfile from '../../../../components/Profile/ModalProfile';
import { PayRatesForm } from '../../../../components/Employee';
import { ApprovalRequests, getApprovalsColMd } from './ApprovalRequests';
import isAdmin from '../../../../helpers/isAdmin';
import { createOptionsPage, OptionsPagedInterface } from '../../../../entities/OptionsPaged';
import EmployeeChangeStatusConfirm from '../../Users/EmployeeChangeStatusConfirm';
import Employee from '../../../../services/Employee';
import FilteredOptionsInterface, { FilterInterface } from '../../../../entities/FilteredOptions';
import confirmAlertWithForm from '../../../../helpers/confirmAlertWithForm';
import isUpperManagement from '../../../../helpers/isUpperManagement';
import isEducationDirectorAdminAssistant from '../../../../helpers/isEducationDirectorAdminAssistant';
import isHRUser from '../../../../helpers/isHRUser';
import isFinanceUser from '../../../../helpers/isFinanceUser';
import { isDirector } from '../../../../helpers';
import isAuthorized from '../../../../helpers/isAuthorized';
import TerminatedEmployeeFormBasicInfo from '../TerminatedEmployeeFormBasicInfo';
import { EmployeesOverviewCardInfo } from './EmployeesOverviewCardInfo';
import DirectoryActionsDropdown from './DirectoryActionsDropdown';
import LeaveOfAbsenceRecordsModal from './LeaveOfAbsenceRecordsModal';
import buildDirectoryColumns from './DirectoryTableColumns';
import NewHireModalContainer from './NewHireModalContainer';
import ResendPaperworkModal from './ResendPaperworkModal';
import usePutOnLeaveModal from './usePutOnLeaveModal';
import FilterBar, { ActiveFilter, EnumOption, FilterFieldDef } from './DirectoryFilters';
import getRoleDisplayName from '../../../../helpers/getRoleDisplayName';
import useDebounce from '../../../../hooks/useDebounce';

export enum ShowModalModes {
  NewHireApproval = 1,
  RaiseApproval = 2,
  DeniedReason = 3,
}

export type DirectoryParameters = {
  showModal?: ShowModalModes;
};

const DIRECTORY_FILTER_FIELDS: FilterFieldDef[] = [
  { key: 'status', label: 'Status', type: 'enum', icon: 'fa-circle', backendField: 'status' },
  { key: 'role', label: 'Role', type: 'enum', icon: 'fa-briefcase', backendField: 'user.permissionsGroup.name' },
  { key: 'workday', label: 'Type', type: 'enum', icon: 'fa-clock', backendField: 'workday' },
  { key: 'title', label: 'Title', type: 'enum', icon: 'fa-tag', backendField: 'title.name' },
  { key: 'department', label: 'Department', type: 'enum', icon: 'fa-building', backendField: 'department.name' },
  { key: 'supervisor', label: 'Supervisor', type: 'enum', icon: 'fa-user', backendField: 'supervisorId' },
  { key: 'startDate', label: 'Start Date', type: 'daterange', icon: 'fa-calendar', backendField: 'startDate' },
  {
    key: 'terminationDate',
    label: 'Termination Date',
    type: 'daterange',
    icon: 'fa-calendar',
    backendField: 'terminationDate',
  },
];

const STATUS_FALLBACK_OPTIONS: EnumOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'HIRED', label: 'Hired' },
  { value: 'OFFER_SENT', label: 'Offer Sent' },
  { value: 'LOA', label: 'LOA' },
  { value: 'TERMINATED', label: 'Terminated' },
  { value: 'DENIED', label: 'Denied' },
  { value: 'DECLINED', label: 'Declined' },
  { value: 'BLOCKED', label: 'Blocked' },
];
const TYPE_FALLBACK_OPTIONS: EnumOption[] = [
  { value: 'FT', label: 'Full Time' },
  { value: 'PT', label: 'Part Time' },
];

const buildBackendFilters = (active: ActiveFilter[]): FilterInterface[] => {
  const out: FilterInterface[] = [];
  active.forEach((f) => {
    const def = DIRECTORY_FILTER_FIELDS.find((d) => d.key === f.key);
    if (!def?.backendField) return;
    const field = def.backendField;

    if (def.type === 'enum') {
      const vals = f.value as string[];
      if (vals?.length) out.push({ field, operation: 'in', value: vals });
    } else if (def.type === 'text') {
      const v = ((f.value as string) || '').trim();
      if (v) out.push({ field, operation: 'ilike', value: `%${v}%` });
    } else if (def.type === 'daterange') {
      const v = f.value as { from: string; to: string };
      if (v?.from) out.push({ field, operation: '>=', value: `${v.from} 00:00:00` });
      if (v?.to) out.push({ field, operation: '<=', value: `${v.to} 23:59:59` });
    } else if (def.type === 'boolean') {
      out.push({ field, operation: '=', value: f.value });
    }
  });
  return out;
};

const DirectoryList = (props: DirectoryParameters) => {
  const { isMobile } = useCheckMobileScreen();
  const { token, currentSchool, currentFranchisee, user }: any = useContext(AuthContext);
  const history = useHistory();
  const loadListRef = useRef<(() => void | Promise<unknown>) | null>(null);
  const listRequestRef = useRef(0);
  const headerRequestRef = useRef(0);
  const newHiresRequestRef = useRef(0);
  const overviewRequestRef = useRef(0);
  const isFirstOrderRender = useRef(true);
  const {
    modal: putOnLeaveModal,
    handlePutOnLeave: putOnLeaveHandlePutOnLeave,
    handleEndPutOnLeave: putOnLeaveHandleEndPutOnLeave,
  } = usePutOnLeaveModal({
    token: token || '',
    currentSchool,
    loadList: () => {
      loadListRef.current?.();
    },
  });
  const [orderByColumn, setOrderByColumn] = useState<string>('');
  const [orderByDesc, setOrderByDesc] = useState<boolean>(false);
  const { showModal } = props;
  const { id: userId } = useParams<{ id: string }>();

  const handleActivatedEmployee = async (employeeId: string) => {
    if (!employeeId) return;
    try {
      await Employee(token).activated(employeeId);
      toast.success('Saved successfully!');
      refreshAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Is not possible change status!');
    }
  };

  const confirmChangestatus = (e: any, id: string) => {
    e?.preventDefault();

    customAlert({
      customUI: ({ onClose }) => {
        return (
          <EmployeeChangeStatusConfirm id={id} handleActivatedEmployee={handleActivatedEmployee} onClose={onClose} />
        );
      },
    });
  };

  const [data, setData] = useState([] as any[]);
  const [newHiresForApproval, setNewHiresForApproval] = useState<any[]>([]);
  const [terminationsPending, setTerminationsPending] = useState<any[]>([]);
  const [terminationStatusFilter, setTerminationStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [loading, setLoading] = useState(false);
  const [headerLoading, setHeaderLoading] = useState(true);
  const [overviewEmployees, setOverviewEmployees] = useState<any[]>([]);
  const [resendPaperworkOpen, setResendPaperworkOpen] = useState(false);
  const [resendPaperworkEmployee, setResendPaperworkEmployee] = useState<any>(null);
  const [pagedMeta, setPagedMeta] = useState(createPagedMeta());

  const [isNewEmployeeFormOpen, setIsNewEmployeeFormOpen] = useState(false);

  const [isTerminatedEmployeeFormOpen, setIsTerminatedEmployeeFormOpen] = useState(false);
  const [terminatedEmployeeStep, setTerminatedEmployeeStep] = useState(0);
  const [terminationUser, setTerminationUser] = useState<any>();
  const [terminationDate, setTerminationDate] = useState(moment().format('MM/DD/YYYY'));
  const [terminationCategory, setTerminationCategory] = useState('');
  const [willBeRehired, setWillBeRehired] = useState<boolean | null>(null);
  const [terminationReason, setTerminationReason] = useState('');
  const [terminationNotes, setTerminationNotes] = useState('');
  const [terminationUserEmail, setTerminationUserEmail] = useState('');
  const [, setTerminationNameToConfirm] = useState('');

  const [isRevokeHireOpen, setIsRevokeHireOpen] = useState(false);
  const [revokeNameToConfirm, setRevokeNameToConfirm] = useState('');
  const [revokeHireUser, setRevokeHireUser] = useState<any>();

  const [isRevokeTerminationOpen, setIsRevokeTerminationOpen] = useState(false);
  const [revokeTerminationUser, setRevokeTerminationUser] = useState<any>();

  const [isRehireOpen, setIsRehireOpen] = useState(false);
  const [rehireUser, setRehireUser] = useState<any>();
  const [confirmRehire, setConfirmRehire] = useState(false);

  const [modalUpdatePayment, setModalUpdatePayment] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);

  const [payRasesCount, setPayRasesCount] = useState<number>(0);
  const [payRatesListRefreshKey, setPayRatesListRefreshKey] = useState<number>(0);

  const [, setPtoBalance] = useState<number>(0);

  const [loaRecordsModalOpen, setLoaRecordsModalOpen] = useState(false);
  const [loaRecordsModalPayload, setLoaRecordsModalPayload] = useState<{
    workerName: string;
    loaRecords: any[];
  } | null>(null);

  useMemo(() => {
    if (!userId || showModal !== ShowModalModes.DeniedReason) {
      return null;
    }

    const load = async () => {
      const filteredUser: FilteredOptionsInterface = {
        filters: [
          {
            field: 'userId',
            operation: 'hash',
            value: userId,
          },
        ],
        options: createOptionsPage(pagedMeta),
      };

      const result = await EmployeeService(token).listEmployeesBySchool(currentSchool.value, '', filteredUser);
      denyReasonAlert(result?.data[0]?.denyReason || '');
    };

    load();
  }, [userId, currentSchool.value, showModal]);

  const populateOrderBy = (): OptionsPagedInterface => {
    return {
      itemsPerPage: principalFilteredOptions?.options?.itemsPerPage ?? 10,
      page: principalFilteredOptions?.options?.page ?? 1,
      sortBy: orderByColumn ? [orderByColumn] : [],
      sortDesc: [orderByDesc],
      mustSort: !!orderByColumn,
      multiSort: false,
    };
  };

  const [principalFilteredOptions, setPrincipalFilteredOptions] = useState({
    filters: [],
    options: createOptionsPage(pagedMeta),
  } as { filters: FilterInterface[]; options: OptionsPagedInterface });

  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [filterOptions, setFilterOptions] = useState<Record<string, EnumOption[]>>({});

  const optionsFor = (key: string): EnumOption[] => {
    if (key === 'status') return STATUS_FALLBACK_OPTIONS;
    if (key === 'workday') return TYPE_FALLBACK_OPTIONS;
    const fromApi = filterOptions[key] || [];
    if (key === 'role') return fromApi.map((o) => ({ value: o.value, label: getRoleDisplayName(o.value) }));
    return fromApi;
  };

  const handleFiltersChange = (next: ActiveFilter[]) => {
    setActiveFilters(next);
    setPrincipalFilteredOptions({
      filters: buildBackendFilters(next),
      options: createOptionsPage({ ...pagedMeta, currentPage: 1 }, populateOrderBy()),
    });
  };

  useEffect(() => {
    const getAllEmployeeInfo = async (user: any) => {
      const employee = await EmployeeService(token || '').list({
        filters: [
          {
            field: 'userId',
            operation: 'hash',
            value: user.id,
          },
        ],
        options: createOptionsPage(pagedMeta),
      });
      const ptoBalanceValue = employee?.data[0]?.ptoPolicy?.currentBalance;
      setPtoBalance(ptoBalanceValue != null && !isNaN(Number(ptoBalanceValue)) ? Number(ptoBalanceValue) : 0);
    };

    if (terminationUser) {
      getAllEmployeeInfo(terminationUser?.user);
      setTerminationUserEmail(terminationUser?.user?.email);
    }

    handleResetTerminationInfo();
  }, [terminationUser]);

  const handleResetTerminationInfo = () => {
    setTerminationDate(moment().format('MM/DD/YYYY'));
    setTerminationCategory('');
    setWillBeRehired(null);
    setTerminationReason('');
    setTerminationNameToConfirm('');
    setTerminationNotes('');
  };

  const handleTerminateUserOpen = async (user: any) => {
    if (user.id === terminationUser?.id) {
      toogleTerminatedEmployeeForm();
      return;
    }

    setTerminationUser(user);
    setTerminatedEmployeeStep(0);
    toogleTerminatedEmployeeForm();
  };

  const handleRevokeUserOpen = async (user: any) => {
    if (user.id === terminationUser?.id) {
      toogleRevokeHire();
      return;
    }

    setRevokeNameToConfirm('');
    setRevokeHireUser(user);
    toogleRevokeHire();
  };

  const handleOpenByParams = async (data: any[]) => {
    const queryParams = new URLSearchParams(window.location.search);
    let idParam = queryParams.get('id');
    let typeParam = queryParams.get('type');
    if (!idParam || !typeParam) return;
    idParam = String(idParam).trim();
    typeParam = String(typeParam).trim().toLowerCase();
    const userFromList = data.find((d) => d?.id === idParam);
    if (
      userFromList &&
      typeParam === 'revoke' &&
      isAuthorized({ moduleName: 'EmployeeTerminate', functionName: 'Revoke' }, user)
    ) {
      await handleRevokeTerminationOpen(userFromList);
    }
    if (userFromList && typeParam === 'rehire') {
      await handleRehireOpen(userFromList);
    }
    if (idParam || typeParam) {
      setTimeout(() => {
        history.replace({
          search: '',
        });
      }, 2000);
    }
  };

  const handleRevokeTerminationOpen = async (user: any) => {
    setRevokeTerminationUser(user);
    toogleRevokeTermination();
  };

  const handleRehireOpen = async (user: any) => {
    setRehireUser(user);
    toogleRehire();
  };

  const handleResendEmail = async (e: React.FormEvent, user: any) => {
    if (user?.id) {
      e?.preventDefault();
      const id = user?.id || '';
      return confirmAlert({
        title: 'Confirm',
        messageType: 'EDIT',
        message: 'Are you sure you want to resend the email?',
        id,
        onClickYes: () => resendEmail(id),
      });
    }
  };

  const resendCredentialsAlert = (e: any, id: string) => {
    if (data?.constructor?.name === 'SyntheticBaseEvent') e?.preventDefault();

    return confirmAlertWithForm({
      title: 'Credentials',
      messageType: 'EDIT',
      message: 'Are you sure you want to resend the credentials email?',
      onSubmit: async (e: any) => {
        e?.preventDefault();
        setLoading(true);
        try {
          await EmployeeService(token).resendCredentials(id);
          toast.success('Credentials Resent successfully!');
        } catch (e: any) {
          toast.error(e?.response?.data?.message || 'Failed to cancel pto request');
          console.error(e?.response?.data?.message || 'Failed to cancel pto request');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const resendPaperworkAlert = (e: any, row: any) => {
    e?.preventDefault();
    setResendPaperworkEmployee(row);
    setResendPaperworkOpen(true);
  };

  const triggerAnnualReviewAlert = (e: any, row: any) => {
    e?.preventDefault();
    const employeeName =
      (row?.firstName && row?.lastName ? `${row.firstName} ${row.lastName}` : row?.fullName) || 'this employee';
    return confirmAlert({
      title: 'Send Annual Review',
      messageType: 'EDIT',
      message: `Send the annual review email to ${employeeName} and management now? This kicks off the cycle.`,
      onClickYes: async () => {
        setLoading(true);
        try {
          await AnnualReviewService(token).triggerKickoff(row.id);
          toast.success('Annual review sent successfully!');
        } catch (e: any) {
          toast.error(e?.response?.data?.message || 'Failed to send annual review');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleUpdatePayment = async (e: React.FormEvent, user: any) => {
    if (user?.id) {
      e?.preventDefault();
      const id = user?.id || '';
      return confirmAlert({
        title: 'Confirm',
        messageType: 'EDIT',
        message: 'Are you sure you want to update payment?',
        id,
        onClickYes: () => {
          setSelectedUser({ ...{ employeeId: user.id }, ...user.user });
          setModalUpdatePayment(!modalUpdatePayment);
        },
      });
    }
  };

  const handleRemoveAccess = async (e: React.FormEvent, user: any) => {
    if (user?.id) {
      e?.preventDefault();
      const id = user?.id || '';
      return confirmAlert({
        title: 'Confirm',
        messageType: 'EDIT',
        message: 'Are you sure you want to remove this access?',
        id,
        onClickYes: () => removeAccess(id),
      });
    }
  };

  const revokeOffer = async () => {
    const id = revokeHireUser?.id || '';
    await EmployeeService(token).revokeNewHire(id);
    toast.success('Saved successfully!');
    setIsRevokeHireOpen(false);
    refreshAll();
  };

  const resendEmail = async (id: string) => {
    await EmployeeService(token).resendNewHire(id);
    toast.success('Sent successfully!');
  };

  const removeAccess = async (id: string) => {
    await EmployeeService(token).removeAccess(id);
    toast.success('Saved successfully!');
    refreshAll();
  };

  const toogleNewEmployeeForm = () => setIsNewEmployeeFormOpen(!isNewEmployeeFormOpen);

  const toogleTerminatedEmployeeForm = () => setIsTerminatedEmployeeFormOpen(!isTerminatedEmployeeFormOpen);

  const toogleRevokeHire = () => setIsRevokeHireOpen(!isRevokeHireOpen);

  const toogleRevokeTermination = () => {
    setIsRevokeTerminationOpen(!isRevokeTerminationOpen);
    refreshAll();
  };

  const toogleRehire = () => setIsRehireOpen(!isRehireOpen);

  const _handleSubmitTerminatedUser = async (event: React.FormEvent) => {
    event?.preventDefault();

    if (terminatedEmployeeStep === 0) {
      setTerminatedEmployeeStep(1);
      return;
    }

    try {
      const data: any = {
        employeeId: terminationUser?.id,
        schoolId: currentSchool.value,
        lastWorkDay: terminationDate,
        category: terminationCategory,
        willBeRehired,
        reason: terminationReason,
        email: terminationUserEmail,
      };

      if (terminationNotes) {
        data.notes = terminationNotes;
      }

      await EmployeeTerminate(token).create(data);

      handleResetTerminationInfo();
      setTerminatedEmployeeStep(0);
      toogleTerminatedEmployeeForm();
      refreshAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Request error');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentSchool?.value) loadList();
  }, [currentSchool, currentFranchisee, debouncedSearch, principalFilteredOptions]);

  useEffect(() => {
    if (!currentSchool?.value) return;
    loadHeader();
    openByUrlParams();
  }, [currentSchool, currentFranchisee]);

  useEffect(() => {
    if (!isRehireOpen) {
      setConfirmRehire(false);
    }
  }, [isRehireOpen]);

  useEffect(() => {
    if (confirmRehire) {
      toogleNewEmployeeForm();
    }
  }, [confirmRehire]);

  const canSeeHeader = () =>
    isAdmin(user) || isUpperManagement(user) || isHRUser(user) || isFinanceUser(user) || isDirector(user);

  const canSeeTerminations = () =>
    isEducationDirectorAdminAssistant(user) ||
    isAdmin(user) ||
    isHRUser(user) ||
    isDirector(user) ||
    isUpperManagement(user);

  const fetchPayRatesCount = async (): Promise<number> => {
    try {
      const filteredOptions = {
        filters: [],
        options: createOptionsPage(createPagedMeta(), { sortDesc: 'desc' }),
      };
      const response = await EmployeeService(token).listPayRatesBySchool(currentSchool.value, filteredOptions);
      return response?.data?.filter((r: any) => r.status === 'Waiting').length || 0;
    } catch (e: any) {
      console.error(e?.response?.data?.message || 'No data Found!');
      return 0;
    }
  };

  const fetchNewHiresForApproval = async (): Promise<any[]> => {
    try {
      const filteredOptions = {
        filters: [{ field: 'status', operation: 'in', value: ['PENDING'] }],
        options: createOptionsPage(createPagedMeta({ perPage: 1000 }), populateOrderBy()),
      } as { filters: FilterInterface[]; options: OptionsPagedInterface };
      const result = await EmployeeService(token).listEmployeesBySchool(currentSchool.value, '', filteredOptions);
      return result?.data || [];
    } catch (e: any) {
      console.error(e?.response?.data?.message || 'No data Found!');
      return [];
    }
  };

  const fetchTerminationsPending = async (): Promise<any[]> => {
    if (!canSeeTerminations()) return [];
    try {
      const statusParam = terminationStatusFilter || undefined;
      const listResult = await EmployeeTerminate(token).listPending(currentSchool.value, statusParam, !!statusParam);
      return Array.isArray(listResult) ? listResult : [];
    } catch (_e) {
      return [];
    }
  };

  const fetchOverviewEmployees = async (): Promise<any[]> => {
    try {
      const result = await EmployeeService(token).listOverviewBySchool(currentSchool.value);
      return result?.data || [];
    } catch (e: any) {
      console.error(e?.response?.data?.message || 'No data Found!');
      return [];
    }
  };

  const loadHeader = async () => {
    if (!currentSchool?.value || !canSeeHeader()) return;
    const requestId = ++headerRequestRef.current;
    newHiresRequestRef.current++;
    overviewRequestRef.current++;
    setHeaderLoading(true);
    const [newHires, terminations, payRatesCount, employees] = await Promise.all([
      fetchNewHiresForApproval(),
      fetchTerminationsPending(),
      fetchPayRatesCount(),
      fetchOverviewEmployees(),
    ]);
    if (requestId !== headerRequestRef.current) return;
    setNewHiresForApproval(newHires);
    setTerminationsPending(terminations);
    setPayRasesCount(payRatesCount);
    setOverviewEmployees(employees);
    setHeaderLoading(false);
  };

  const reloadNewHiresForApproval = async () => {
    if (!currentSchool?.value || !canSeeHeader()) return;
    const requestId = ++newHiresRequestRef.current;
    const newHires = await fetchNewHiresForApproval();
    if (requestId !== newHiresRequestRef.current) return;
    setNewHiresForApproval(newHires);
  };

  const reloadOverviewEmployees = async () => {
    if (!currentSchool?.value || !canSeeHeader()) return;
    const requestId = ++overviewRequestRef.current;
    const employees = await fetchOverviewEmployees();
    if (requestId !== overviewRequestRef.current) return;
    setOverviewEmployees(employees);
  };

  const openByUrlParams = async () => {
    const queryParams = new URLSearchParams(window.location.search);
    const idParam = queryParams.get('id');
    if (!idParam || !queryParams.get('type') || !canSeeHeader()) return;
    try {
      const filteredOptions = {
        filters: [{ field: 'id', operation: 'hash', value: String(idParam).trim() }],
        options: createOptionsPage(createPagedMeta()),
      } as { filters: FilterInterface[]; options: OptionsPagedInterface };
      const result = await EmployeeService(token).listEmployeesBySchool(currentSchool.value, '', filteredOptions);
      handleOpenByParams(result?.data || []);
    } catch (e: any) {
      console.error(e?.response?.data?.message || 'No data Found!');
    }
  };

  const refreshAll = () => {
    loadList();
    loadHeader();
  };

  useEffect(() => {
    if (isFirstOrderRender.current) {
      isFirstOrderRender.current = false;
      return;
    }
    const newFilteredOptions = {
      filters: principalFilteredOptions.filters,
      options: createOptionsPage(pagedMeta, populateOrderBy()),
    };
    setPrincipalFilteredOptions(newFilteredOptions);
    reloadNewHiresForApproval();
  }, [orderByColumn, orderByDesc]);

  const loadList = async () => {
    if (!currentSchool?.value) return;
    const requestId = ++listRequestRef.current;
    try {
      setLoading(true);
      const result = await EmployeeService(token).listEmployeesBySchool(
        currentSchool.value,
        debouncedSearch,
        principalFilteredOptions,
      );
      if (requestId !== listRequestRef.current) return;

      const newData = result.data;
      if (JSON.stringify(newData) !== JSON.stringify(data)) {
        setData(newData);
        const meta: PagedMetaInterface = result.meta;
        if (meta) {
          setPagedMeta(meta);
        }
      }

      const apiFilterOptions = result?.filterOptions || result?.meta?.filterOptions;
      if (apiFilterOptions) {
        setFilterOptions(apiFilterOptions);
      }
    } catch (e: any) {
      if (requestId !== listRequestRef.current) return;
      const message = String(e?.response?.data?.message || 'Error fetching list');
      return toast.error(message);
    } finally {
      if (requestId === listRequestRef.current) {
        setLoading(false);
      }
    }
  };

  loadListRef.current = refreshAll;

  const loadTerminations = async (status?: string) => {
    if (
      !currentSchool?.value ||
      !(
        isEducationDirectorAdminAssistant(user) ||
        isAdmin(user) ||
        isHRUser(user) ||
        isDirector(user) ||
        isUpperManagement(user)
      )
    ) {
      return;
    }
    try {
      const statusParam = status !== undefined ? status : terminationStatusFilter;
      const filterStatus = statusParam || undefined;
      const includeHidden = !!filterStatus;
      const listResult = await EmployeeTerminate(token).listPending(currentSchool.value, filterStatus, includeHidden);
      setTerminationsPending(Array.isArray(listResult) ? listResult : []);
    } catch (_e) {
      setTerminationsPending([]);
    }
  };

  const onTerminationStatusFilterChange = (status: string) => {
    setTerminationStatusFilter(status);
    loadTerminations(status);
  };

  const showTerminationInfosAlert = async (emp: any) => {
    console.log(emp);
    const employee = await EmployeeService(token || '').list({
      filters: [
        {
          field: 'userId',
          operation: 'hash',
          value: emp.userId,
        },
      ],
      options: createOptionsPage(pagedMeta),
    });

    return confirmAlertWithForm({
      className: 'w-500',
      title: '',
      messageType: 'INFO',
      message: ' ',
      buttons: {
        confirm: { label: 'Export', hidden: true },
        cancel: { label: 'Close', hidden: false },
      },
      children: (
        <TerminatedEmployeeFormBasicInfo
          terminationDate={emp?.terminationDetails[0]?.createdAt}
          terminationCategory={emp?.terminationDetails[0]?.category}
          terminationReason={emp?.terminationDetails[0]?.reason}
          terminationNotes={emp?.terminationDetails[0]?.notes}
          ptoBalance={(() => {
            const ptoBalanceValue = employee?.data[0]?.ptoPolicy?.currentBalance;
            return ptoBalanceValue != null && !isNaN(Number(ptoBalanceValue)) ? Number(ptoBalanceValue) : 0;
          })()}
        />
      ),
      onSubmit: (e: any) => {
        console.log(e);
      },
    });
  };

  const showLoaInfosAlert = async (emp: any) => {
    try {
      const response = await EmployeeService(token || '').getPutOnLeaveDetails(emp.id);

      let loaRecords = [];
      if (response?.data?.data) {
        loaRecords = Array.isArray(response.data.data) ? response.data.data : [];
      }

      const workerName = `${emp?.user?.firstName || ''} ${emp?.user?.lastName || ''}`.trim();

      setLoaRecordsModalPayload({ workerName, loaRecords });
      setLoaRecordsModalOpen(true);
    } catch (error: any) {
      if (error?.response?.status === 404 || error?.response?.status === 400) {
        const workerName = `${emp?.user?.firstName || ''} ${emp?.user?.lastName || ''}`.trim();
        setLoaRecordsModalPayload({ workerName, loaRecords: [] });
        setLoaRecordsModalOpen(true);
        return;
      }
      console.error('Error loading LOA details:', error);
      toast.error('Failed to load LOA details');
    }
  };

  const denyReasonAlert = async (reason: string) => {
    customAlert({
      customUI: ({ onClose }) => {
        return (
          <Row className="mt-4">
            <Col>
              <FormGroup className="form-control-label">
                <Label>Reason</Label>
                <Input type="textarea" name="reason" value={reason} disabled={true} />
              </FormGroup>
            </Col>
            <Col md="12 text-right">
              <Button
                color="primary"
                href="#"
                onClick={() => {
                  onClose();
                }}
                size="sm"
                style={{ minWidth: '100px' }}
              >
                OK
              </Button>
            </Col>
          </Row>
        );
      },
    });
  };

  const renderActions = (row: any) => (
    <DirectoryActionsDropdown
      row={row}
      user={user}
      isMobile={isMobile}
      handlers={{
        resendPaperworkAlert,
        resendCredentialsAlert,
        handleRemoveAccess,
        handleTerminateUserOpen,
        handlePutOnLeave: putOnLeaveHandlePutOnLeave,
        handleUpdatePayment,
        handleEndPutOnLeave: putOnLeaveHandleEndPutOnLeave,
        handleRevokeTerminationOpen,
        handleRevokeUserOpen,
        handleResendEmail,
        handleRehireOpen,
        confirmChangestatus,
        triggerAnnualReviewAlert,
      }}
    />
  );

  const columns = buildDirectoryColumns({
    isMobile,
    actions: renderActions,
    showLoaInfosAlert,
    showTerminationInfosAlert,
    denyReasonAlert,
    isEducationDirectorAdminAssistant: isEducationDirectorAdminAssistant(user),
    columnHeaderConfig: {
      orderByColumn,
      orderByDesc,
      setOrderByColumn,
      setOrderByDesc,
    },
  });

  const handlePerRowsChange = async (perPage: number, page: number) => {
    const newOptions = {
      ...pagedMeta,
      perPage,
      currentPage: page,
    };
    const newFilteredOptions = {
      filters: principalFilteredOptions.filters,
      options: createOptionsPage(newOptions, populateOrderBy()),
    };
    setPrincipalFilteredOptions(newFilteredOptions);
  };

  const handlePageChange = async (page: number) => {
    const newOptions = {
      ...pagedMeta,
      currentPage: page,
    };
    const newFilteredOptions = {
      filters: principalFilteredOptions.filters,
      options: createOptionsPage(newOptions, populateOrderBy()),
    };
    setPrincipalFilteredOptions(newFilteredOptions);
  };

  return (
    <>
      <CommonHeader />
      {isAdmin(user) || isUpperManagement(user) || isHRUser(user) || isFinanceUser(user) || isDirector(user) ? (
        <Container className="mt--8 mb-9" fluid>
          <Row className="justify-content-center mt-3">
            <Col xs="12" md={getApprovalsColMd(user)}>
              <ApprovalRequests
                newHires={(newHiresForApproval || []).filter((d) => d.status === 'PENDING')}
                loading={headerLoading}
                loadList={refreshAll}
                payRasesCount={payRasesCount}
                terminationsPending={terminationsPending}
                schoolId={currentSchool?.value ?? ''}
                showModal={showModal && showModal !== ShowModalModes.DeniedReason ? showModal : undefined}
                payRatesListRefreshKey={payRatesListRefreshKey}
                terminationStatusFilter={terminationStatusFilter}
                onTerminationStatusFilterChange={onTerminationStatusFilterChange}
                onModalClose={refreshAll}
              />
            </Col>
          </Row>
          <Row className="justify-content-center mt-3">
            <Col xs="12" md="8">
              <EmployeesOverviewCardInfo
                employees={overviewEmployees}
                loading={headerLoading}
                renderActions={renderActions}
                onModalToggle={reloadOverviewEmployees}
              />
            </Col>
          </Row>
        </Container>
      ) : (
        <></>
      )}

      {resendPaperworkOpen && resendPaperworkEmployee && (
        <ResendPaperworkModal
          isOpen={resendPaperworkOpen}
          toggle={() => setResendPaperworkOpen(false)}
          employee={resendPaperworkEmployee}
          onSuccess={() => {
            setResendPaperworkEmployee(null);
            refreshAll();
          }}
        />
      )}

      <ModalProfile
        className="modal-dialog-centered"
        isOpen={modalUpdatePayment}
        toggle={() => {
          setModalUpdatePayment(!modalUpdatePayment);
        }}
        size={'xs'}
        user={selectedUser}
      >
        <PayRatesForm
          id={selectedUser?.employeeId}
          onSubmit={(_: any) => {
            setModalUpdatePayment(!modalUpdatePayment);
            loadHeader();
            setPayRatesListRefreshKey((prev) => prev + 1);
          }}
        />
      </ModalProfile>
      <NewHireModalContainer
        isOpen={isNewEmployeeFormOpen}
        toggle={toogleNewEmployeeForm}
        token={token || ''}
        currentSchool={currentSchool}
        currentFranchisee={currentFranchisee}
        user={user}
        onSuccess={refreshAll}
        rehireUser={rehireUser}
        setRehireUser={setRehireUser}
        confirmRehire={confirmRehire}
        setConfirmRehire={setConfirmRehire}
      />
      {/* <TerminatedEmployeeForm
        isTerminatedEmployeeFormOpen={isTerminatedEmployeeFormOpen}
        toogleTerminatedEmployeeForm={toogleTerminatedEmployeeForm}
        handleSubmitTerminatedUser={handleSubmitTerminatedUser}
        terminatedEmployeeStep={terminatedEmployeeStep}
        terminationUser={terminationUser}
        terminationDate={terminationDate}
        setTerminationDate={setTerminationDate}
        terminationCategory={terminationCategory}
        setTerminationCategory={setTerminationCategory}
        willBeRehired={willBeRehired}
        setWillBeRehired={setWillBeRehired}
        terminationReason={terminationReason}
        setTerminationReason={setTerminationReason}
        terminationNotes={terminationNotes}
        setTerminationNotes={setTerminationNotes}
        terminationUserEmail={terminationUserEmail}
        setTerminationUserEmail={setTerminationUserEmail}
        terminationNameToConfirm={terminationNameToConfirm}
        setTerminationNameToConfirm={setTerminationNameToConfirm}
        setTerminatedEmployeeStep={setTerminatedEmployeeStep}
        ptoBalance={ptoBalance}
      /> */}
      <TerminatedEmployeeFormV2
        isOpen={isTerminatedEmployeeFormOpen}
        toggle={toogleTerminatedEmployeeForm}
        employee={terminationUser}
        schoolId={currentSchool.value}
        token={token}
        onSuccess={refreshAll}
      />
      <RevokeOfferForm
        isRevokeHireOpen={isRevokeHireOpen}
        toogleRevokeHire={toogleRevokeHire}
        revokeHireUser={revokeHireUser}
        revokeNameToConfirm={revokeNameToConfirm}
        setRevokeNameToConfirm={setRevokeNameToConfirm}
        handleSubmit={(e: any) => {
          e?.preventDefault();
          revokeOffer();
        }}
      />
      <RevokeTerminationForm
        isOpen={isRevokeTerminationOpen}
        toogle={toogleRevokeTermination}
        revokeUser={revokeTerminationUser}
      />
      <RehireForm
        isOpen={isRehireOpen}
        toogle={toogleRehire}
        user={rehireUser}
        handleSubmit={(e: any) => {
          e?.preventDefault();
          setConfirmRehire(true);
          toogleRehire();
        }}
      />

      {putOnLeaveModal}

      <LeaveOfAbsenceRecordsModal
        isOpen={loaRecordsModalOpen}
        toggle={() => {
          setLoaRecordsModalOpen(false);
          setLoaRecordsModalPayload(null);
        }}
        workerName={loaRecordsModalPayload?.workerName}
        loaRecords={loaRecordsModalPayload?.loaRecords ?? []}
      />

      <Container className="mt--8" fluid>
        <Card className="bg-secondary shadow">
          <CardHeader>
            <h2 className="mb-3">Directory List</h2>
            <Row>
              <Col md="8" xs="12">
                <Input
                  type="text"
                  placeholder="Search by Name"
                  style={{ width: '100%' }}
                  value={search}
                  onChange={(event: any) => setSearch(event.target.value)}
                />
              </Col>
              {!isMobile &&
              !isEducationDirectorAdminAssistant(user) &&
              isAuthorized({ moduleName: 'EmployeeNewHire', functionName: 'Create' }, user) ? (
                <Col md="4" xs="4">
                  <Button className="btn" color="primary" onClick={toogleNewEmployeeForm}>
                    <i className="fas fa-plus mr-2" />
                    Create New
                  </Button>
                </Col>
              ) : (
                <></>
              )}
            </Row>
            <Row>
              <Col xs="12" className="mt-3">
                <FilterBar
                  fields={DIRECTORY_FILTER_FIELDS}
                  filters={activeFilters}
                  onChange={handleFiltersChange}
                  optionsFor={optionsFor}
                />
              </Col>
            </Row>
          </CardHeader>

          <CardBody style={isMobile ? { paddingLeft: 5, paddingRight: 5 } : {}}>
            <Row>
              <Col xs="12">
                <style>{`
                  .directory-sticky-table .rdt_TableHead {
                    z-index: 5 !important;
                  }
                  .directory-sticky-table .rdt_TableCol:first-of-type {
                    position: sticky;
                    left: 0;
                    z-index: 3;
                    background-color: #F6F9FC;
                  }
                `}</style>
                <div className="directory-sticky-table">
                  <DataTable
                    columns={columns}
                    data={data}
                    totalRows={pagedMeta.total}
                    loading={loading}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onChangePage={handlePageChange}
                    fixedHeader={!isMobile}
                    fixedHeaderScrollHeight={!isMobile ? 'calc(100vh - 320px)' : undefined}
                  />
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default DirectoryList;
